import React from 'react';
import { Radio, Youtube, ExternalLink, Calendar, Clock, Tv } from 'lucide-react';

interface LiveStreamSectionProps {
  canalYoutubeUrl?: string;
  fechaRango?: string;
  horaApertura?: string;
}

export const LiveStreamSection: React.FC<LiveStreamSectionProps> = ({
  canalYoutubeUrl = 'https://www.youtube.com/@martillerosba',
  fechaRango = '26 y 27 de Noviembre 2026',
  horaApertura = 'Desde las 08:30 hs'
}) => {
  return (
    <section id="streaming" className="py-20 bg-slate-50 relative border-t border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-navy via-[#0B3B66] to-brand-navy p-8 sm:p-12 shadow-xl shadow-brand-navy/10 text-white">
          {/* Glows ambientales */}
          <div className="absolute top-0 right-0 -mt-12 -mr-12 w-80 h-80 bg-brand-cyan/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 -mb-12 -ml-12 w-80 h-80 bg-red-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Información del Stream */}
            <div className="lg:col-span-7 space-y-5 text-left">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-bold uppercase tracking-wider">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                </span>
                <Radio className="w-3.5 h-3.5 ml-0.5" />
                <span>Congreso en Vivo</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
                Transmisión Oficial en Directo
              </h2>

              <p className="text-slate-200 text-sm sm:text-base leading-relaxed max-w-xl">
                Seguí todas las disertaciones magistrales, debates y paneles temáticos en tiempo real con calidad de alta definición a través de nuestro canal institucional oficial.
              </p>

              <div className="flex flex-wrap items-center gap-4 text-xs font-semibold text-slate-300 pt-1">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 border border-white/10">
                  <Calendar className="w-4 h-4 text-brand-cyan" />
                  <span>{fechaRango}</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 border border-white/10">
                  <Clock className="w-4 h-4 text-brand-cyan" />
                  <span>{horaApertura}</span>
                </div>
              </div>

              <div className="pt-3">
                <a
                  href={canalYoutubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-6 py-3.5 rounded-xl bg-red-600 hover:bg-red-500 text-white font-bold text-sm transition-all shadow-lg shadow-red-600/30 active:scale-95 leading-snug max-w-xl text-left"
                >
                  <Youtube className="w-5 h-5 shrink-0" />
                  <span>Accedé y suscribite a nuestro canal de YouTube donde podrá estar conectado a la transmisión y videos del evento.</span>
                  <ExternalLink className="w-4 h-4 shrink-0 opacity-80 ml-auto hidden sm:block" />
                </a>
              </div>
            </div>

            {/* Preview Card / Placeholder audiovisual */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="w-full max-w-md aspect-video rounded-2xl bg-slate-900/90 border border-white/15 p-6 flex flex-col items-center justify-center text-center shadow-2xl relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-900/60 pointer-events-none" />
                
                <div className="w-16 h-16 rounded-2xl bg-red-600/20 border border-red-500/30 flex items-center justify-center text-red-400 mb-3 group-hover:scale-110 transition-transform relative z-10">
                  <Tv className="w-8 h-8" />
                </div>

                <p className="text-white font-bold text-base relative z-10">
                  Canal Oficial MartillerosBA
                </p>
                <p className="text-slate-400 text-xs mt-1 relative z-10">
                  La señal se habilitará automáticamente al iniciar la apertura.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
